/**
 * This is float-window script.js file
 */
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';

export default {
  name: 'FloatWindow',
  props: {
    tabs: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  components: {
    VuePdfEmbed
  },

  computed: {
    /**
     * Los videos individuales de anverso/reverso de Finveris no se validan
     * desde esta ventana (solo la videoidentificación completa)
     * @return {Boolean} true si el documento mostrado es un video de anverso/reverso
     */
    isFrontBackVideo() {
      return ['VIDEO_IDENTIFICATIVO_PF_ANVERSO', 'VIDEO_IDENTIFICATIVO_PF_REVERSO'].includes(
        this.tabs.key_subcategory
      );
    }
  },

  /**
   * @override
   */
  data() {
    return {
      filePreview: null,
      fileName: null,
      fileType: null,
      numPages: null,
      actions: ['Minimize', 'Close'],
      displayWindow: false,
      dynamicStyle: this.setDynamicStyle(),
      loading: false,
      useFallback: false,
      // Unique identifier for this component instance
      instanceId: `${this.tabs.uuid}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  },

  /**
   * @override
   */
  beforeDestroy() {
    // Clean up object URLs to prevent memory leaks
    if (this.filePreview && typeof this.filePreview === 'string' && this.filePreview.startsWith('blob:')) {
      window.URL.revokeObjectURL(this.filePreview);
    }
  },

  /**
   * @override
   */
  mounted() {
    const name_entity = this.tabs.type.type; // interveners, accounts ...
    const uuid_entity = this.tabs.type.uuid;
    const uuid_document = this.tabs.uuid;
    this.$documentsService
      .get(name_entity, uuid_entity, uuid_document)
      .then(res => {
        this.fileType = res.type !== 'application/octet-stream' ? res.type : 'application/pdf';
        this.fileName = this.tabs.title;
        
        if (this.fileType === 'application/pdf') {
          // Try creating object URL for vue-pdf-embed
          const urlCreator = window.URL || window.webkitURL;
          this.filePreview = urlCreator.createObjectURL(res);
        } else {
          // For other files (images, videos), create object URL
          const urlCreator = window.URL || window.webkitURL;
          this.filePreview = urlCreator.createObjectURL(res);
        }
        
        // Delay window display to ensure Kendo UI is ready
        this.$nextTick(() => {
          this.displayWindow = true;
        });
      })
      .catch(() => {
        this.displayWindow = false;
        this.$toast.error(`Documento no encontrado`, `No se muestra la pantalla emergente`);
        this.$store.dispatch('bastanteoStore/removeFloatWindow', this.tabs.uuid);
      });
  },
  methods: {
    setDynamicStyle() {
      const style = {};
      const floatWindWidth = 460;

      const width = this.index * floatWindWidth;
      const maxWidth = Math.floor(window.innerWidth / floatWindWidth) * floatWindWidth;
      const newH = Math.floor(width / maxWidth);
      let newW = width - maxWidth;

      style['padding-left'] = `${width}px`;

      if (width >= maxWidth || newW >= maxWidth) {
        if (newH > 0) {
          newW = width - maxWidth * newH;
        }
        style['padding-left'] = `${newW}px`;
        style['padding-top'] = `${newH * 50}px`;
      }

      return style;
    },

    onboardingLink(uuid) {
      const onboardingOrigin = process.env.VUE_APP_ONBOARDING_HOST;
      if (uuid) {
        return new URL(`reupload-documents?uuid=${uuid}`, onboardingOrigin).toString();
      } else {
        return new URL(`digital-error`, onboardingOrigin).toString();
      }
    },
    /**
     * Cerramos ventana
     */
    validate() {
      if (this.tabs.blocking_issues > 0 || this.tabs.is_rejected) {
        return;
      }
      this.loading = true
      this.$intervenersService.getIntervenerDocuments(this.tabs.uuid_intervener).then(res => {
        const identificative_documents = res.data.intervener_documents.filter(
          x => x.key_subcategory === 'VIDEOIDENTIFICACION' && x.uuid === this.tabs.uuid
        );

        identificative_documents.forEach(element => {
          const payload = {
            uuid: element.uuid_intervener,
            uuidDocument: element.uuid,
            data: { validated: true }
          };

          this.$documentsService
            .update('interveners', payload)
            .then(() => {
              this.$root.$emit('refresh_intervener');
              this.$root.$emit('refresh_account');
              this.$root.$emit('refresh_operation');
              const windowRef = this.$refs[this.instanceId];
              if (windowRef) {
                windowRef.destroyKendoWidget();
                this.$store.dispatch('bastanteoStore/removeFloatWindow', this.tabs.uuid);
              }
              this.$toast.success(`Validado`, `Documento validado con exito.`);
            })
            .catch(err => {
              this.$toast.error('Error de autenticación', `Fallo al validar el documento: ${err}`);
            })
            .finally(() => this.loading = false)
        });
      });
    },

    /**
     * PDF loaded successfully
     */
    onPdfLoaded() {
      // PDF loaded successfully
    },

    /**
     * PDF loading failed
     */
    onPdfLoadingFailed() {
      this.useFallback = true;
      // Don't show error toast immediately, let iframe try first
    },

    async confirmRejectVideoId() {
      this.$bvModal.hide('modalRejectVideoIdFloat');
      await this.rejectVideoId();
    },

    async rejectVideoId() {
      if (!this.tabs.uuid_intervener) return;
      this.loading = true;
      try {
        await this.$intervenersService.rejectVideoId(this.tabs.uuid_intervener);
        this.$toast.success('VideoID rechazado', 'Se ha enviado un nuevo enlace al interviniente.');
        this.$root.$emit('refresh_intervener');
        this.$root.$emit('refresh_account');
        this.$root.$emit('refresh_operation');
        this.$root.$emit('refresh_bastanteo_groups');
        const windowRef = this.$refs[this.instanceId];
        if (windowRef) {
          windowRef.destroyKendoWidget();
        }
        this.$store.dispatch('bastanteoStore/removeFloatWindow', this.tabs.uuid);
      } catch (err) {
        this.$toast.error('Error', 'No se ha podido rechazar la videoidentificación.');
      } finally {
        this.loading = false;
      }
    },

    /**
     * Cerramos ventana
     * @param {Object} id Identificador del documento a cerrar
     */
    close() {
      // Clean up Kendo widget properly
      const windowRef = this.$refs[this.instanceId];
      if (windowRef) {
        try {
          windowRef.destroyKendoWidget();
        } catch (error) {
          // Silently handle Kendo widget destruction errors
        }
      }
      
      // Clean up object URL
      if (this.filePreview && typeof this.filePreview === 'string' && this.filePreview.startsWith('blob:')) {
        window.URL.revokeObjectURL(this.filePreview);
        this.filePreview = null;
      }
      
      // Remove from store
      this.$store.dispatch('bastanteoStore/removeFloatWindow', this.tabs.uuid);
    }
  }
};
